import { VideoElement } from '../types/player';
import isMobile from 'ismobilejs';
/**
 * Detects if the current viewport is mobile
 */
export const isBreakPointMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

/**
 * Detects if the current device is a mobile or a tablet
 */
export const isMobileOrTabletDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isMobile(window.navigator).phone || isMobile(window.navigator).tablet;
};

/**
 * Attempts to play media with fallback to muted autoplay
 */
export const attemptPlay = async (mediaElement: VideoElement): Promise<boolean> => {
  if (!mediaElement) {
    return false;
  }

  // Check if play method exists
  if (typeof mediaElement.play !== 'function') {
    return false;
  }

  try {
    await mediaElement.play();
    return true;
  } catch (error) {
    // Store current volume to restore later
    const currentVolume = mediaElement.volume;

    try {
      mediaElement.muted = true;
      await mediaElement.play();

      // If we succeeded with muted but originally had volume,
      // try to unmute after playback has started
      if (currentVolume > 0) {
        // Wait a bit before attempting to unmute to avoid browser restrictions
        setTimeout(() => {
          try {
            mediaElement.muted = false;
          } catch (e) {}
        }, 1000);
      }

      return true;
    } catch (secondError) {
      mediaElement.muted = false;
      return false;
    }
  }
};

/**
 * Get video element from Mux Player (works with nested shadow DOM)
 * This is the most reliable way to get the video element from MuxPlayer
 */
export const getVideoElementFromMux = (): VideoElement => {
  // Find all mux-player elements in the document
  const muxElements = document.querySelectorAll('mux-player');

  if (muxElements.length === 0) {
    return null;
  }

  // Try each mux-player to find a video element
  for (let i = 0; i < muxElements.length; i++) {
    const muxPlayer = muxElements[i];

    try {
      // Check if there's a shadow root
      const shadowRoot = muxPlayer.shadowRoot;
      if (shadowRoot) {
        // Look for media-theme element in the first level shadow root
        const mediaTheme = shadowRoot.querySelector('media-theme');
        if (mediaTheme) {
          // Check if media-theme has its own shadow root
          const mediaThemeShadow = mediaTheme.shadowRoot;
          if (mediaThemeShadow) {
            // Look for mux-video element
            const muxVideo = mediaThemeShadow.querySelector('mux-video');
            if (muxVideo) {
              // Check if mux-video has shadow root
              const muxVideoShadow = muxVideo.shadowRoot;
              if (muxVideoShadow) {
                // Finally check for the actual video element
                const video = muxVideoShadow.querySelector('video');
                if (video) {
                  return video;
                } else {
                  // Check if the video exists directly as a child of mux-video
                  const directVideo = muxVideo.querySelector('video');
                  if (directVideo) {
                    return directVideo;
                  }
                }
              } else {
                // If mux-video doesn't have shadow root, check for direct video
                const video = muxVideo.querySelector('video');
                if (video) {
                  return video;
                }
              }

              // Try to access the video via Mux's MediaController
              try {
                // @ts-expect-error: Is needed because of the shadow DOM
                const mediaController = muxVideo.mediaController;
                if (mediaController && mediaController.media) {
                  return mediaController.media;
                }
              } catch (err) {}
            }
          }
        }
      }
    } catch (err) {}
  }

  // Last resort - try the video API directly
  try {
    for (let i = 0; i < muxElements.length; i++) {
      const muxPlayer = muxElements[i] as any;

      // Try the Mux Player's API methods if available
      if (typeof muxPlayer.play === 'function') {
        // Check if there's an API to get the media element
        if (muxPlayer.mediaEl) {
          return muxPlayer.mediaEl;
        }
      }
    }
  } catch (err) {}

  // If nothing worked, let's create a custom traversal function
  try {
    const findVideoInShadow = (element: Element): HTMLVideoElement | null => {
      // Check if this element is a video
      if (element.tagName.toLowerCase() === 'video') {
        return element as HTMLVideoElement;
      }

      // Check if it has a shadow root
      const shadow = (element as any).shadowRoot;
      if (shadow) {
        // Search all children in the shadow root
        const allElements = shadow.querySelectorAll('*');
        for (let i = 0; i < allElements.length; i++) {
          const result = findVideoInShadow(allElements[i]);
          if (result) return result;
        }
      }

      // Check all children
      const children = element.children;
      for (let i = 0; i < children.length; i++) {
        const result = findVideoInShadow(children[i]);
        if (result) return result;
      }

      return null;
    };

    // Try this recursive search on each mux-player
    for (let i = 0; i < muxElements.length; i++) {
      const result = findVideoInShadow(muxElements[i]);
      if (result) {
        return result;
      }
    }
  } catch (err) {}

  // If we reach here, we couldn't find a video element in any MuxPlayer
  return null;
};
/**
 * Gets the actual video element from a player reference
 */
export const getVideoElementFromRef = (
  playerRef: React.RefObject<HTMLMediaElement>,
): VideoElement => {
  if (!playerRef.current) {
    return null;
  }

  // First check if the ref itself is a video element
  if (playerRef.current.tagName.toLowerCase() === 'video') {
    return playerRef.current;
  }

  // Look for a nested video element
  const video = playerRef.current.querySelector('video');
  if (video) {
    return video;
  }

  // Look for a MuxPlayer element which might contain a video
  if (playerRef.current.tagName.toLowerCase() === 'mux-player') {
    try {
      // Check if there's a mediaEl property
      const mediaEl = (playerRef.current as any).mediaEl;
      if (mediaEl && mediaEl.nodeName === 'VIDEO') {
        return mediaEl;
      }
    } catch (err) {}

    try {
      const shadowRoot = (playerRef.current as any).shadowRoot;
      if (shadowRoot) {
        const shadowVideo = shadowRoot.querySelector('video');
        if (shadowVideo) {
          return shadowVideo;
        }
      }
    } catch (err) {}
  }

  // Fallback to searching for any MuxPlayer in the document
  return getVideoElementFromMux();
};

/**
 * Gets the aspect ratio of a video element
 * Returns null if the dimensions are not available
 */
export const getVideoAspectRatio = (videoElement: HTMLVideoElement | null): number | null => {
  if (!videoElement) {
    return null;
  }

  // Check if the video dimensions are available
  if (!videoElement.videoWidth || !videoElement.videoHeight) {
    return null;
  }

  const ratio = videoElement.videoWidth / videoElement.videoHeight;
  return ratio;
};
